"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRegistry = exports.loadContext = exports.isLibraryTopology = exports.withDefaultOptions = void 0;
const editor_elements_conventions_1 = require("@wix/editor-elements-conventions");
const createResourceLoader_1 = require("./loaders/createResourceLoader");
const createManifestAPI_1 = require("./manifest/createManifestAPI");
const createComponentLoaders_1 = require("./loaders/createComponentLoaders");
const loadExternalRegistry_1 = require("./loaders/loadExternalRegistry");
const validateLibraryURL_1 = require("./toolbox/validateLibraryURL");
const utils_1 = require("./utils");
function withDefaultOptions(options) {
    return Object.assign({ dev: false, useScriptsInsteadOfEval: false, usePartialManifests: false, useExperimentalEval: false, useLazyLoadersInit: false }, options);
}
exports.withDefaultOptions = withDefaultOptions;
const isLibraryTopology = (library) => {
    return library.hasOwnProperty('namespace') && library.hasOwnProperty('url');
};
exports.isLibraryTopology = isLibraryTopology;
function loadContext({ host, libraries, resourceLoader, options, }) {
    return __awaiter(this, void 0, void 0, function* () {
        const urls = [];
        const manifests = [];
        libraries.forEach((library, index) => {
            if (exports.isLibraryTopology(library)) {
                const manifestName = editor_elements_conventions_1.getManifestName({
                    namespace: library.namespace,
                    host,
                    isDev: false,
                });
                const url = `${library.url}/${manifestName}`;
                urls.push({ url, order: index });
            }
            else {
                manifests.push({
                    manifest: createManifestAPI_1.createManifestAPI(library),
                    order: index,
                });
            }
        });
        const loadManifests = () => {
            return Promise.all(urls.map(({ url, order }) => __awaiter(this, void 0, void 0, function* () {
                const manifest = yield resourceLoader.fetchLibraryManifest({
                    url,
                });
                return {
                    order,
                    manifest: createManifestAPI_1.createManifestAPI(manifest),
                };
            })));
        };
        if (!options.usePartialManifests) {
            manifests.push(...(yield loadManifests()));
        }
        return {
            manifests,
            urls: urls.map(({ url }) => url),
            loadManifests: options.usePartialManifests ? loadManifests : null,
        };
    });
}
exports.loadContext = loadContext;
function splitLibraries(libraries) {
    return libraries.reduce((acc, lib) => {
        if (exports.isLibraryTopology(lib) &&
            lib.namespace === loadExternalRegistry_1.EXTERNAL_REGISTRY_OVERRIDE_NAMESPACE) {
            acc.registryOverrideLibrary = lib;
        }
        else {
            acc.libraries.push(lib);
        }
        return acc;
    }, {
        libraries: [],
        registryOverrideLibrary: null,
    });
}
function filterUntrustedURLs(libraries) {
    return libraries.filter(library => {
        if (exports.isLibraryTopology(library)) {
            return validateLibraryURL_1.validateLibraryURL(library.url);
        }
        else {
            return validateLibraryURL_1.validateLibraryURL(library.baseURL);
        }
    });
}
function createRegistry({ host }) {
    return (params) => __awaiter(this, void 0, void 0, function* () {
        const options = withDefaultOptions(params.options || {});
        const resourceLoader = createResourceLoader_1.createResourceLoader({
            options,
            fetcher: params.fetcher,
            globals: params.globals,
        });
        const { libraries, registryOverrideLibrary } = splitLibraries(filterUntrustedURLs(params.libraries));
        if (registryOverrideLibrary) {
            const registry = yield loadExternalRegistry_1.loadExternalRegistry({
                resourceLoader,
                host,
                url: registryOverrideLibrary.url,
            });
            return registry(Object.assign(Object.assign({}, params), { libraries }));
        }
        const context = yield loadContext({
            host,
            libraries,
            resourceLoader,
            options,
        });
        let loaders = {};
        const librariesComponents = {};
        function createComponentLoaders(manifests) {
            return __awaiter(this, void 0, void 0, function* () {
                const tasks = manifests.map(({ order, manifest }) => __awaiter(this, void 0, void 0, function* () {
                    const loadBundle = resourceLoader.createBundleLoader({
                        environment: manifest.getEnvironment(),
                    });
                    // without *.local
                    const namespace = editor_elements_conventions_1.withProdNamespace(manifest.getNamespace());
                    const mode = (params.modes && params.modes[namespace]) || params.mode || 'lazy';
                    // @ts-expect-error
                    librariesComponents[order] =
                        mode === 'lazy'
                            ? createComponentLoaders_1.createLazyComponentLoaders({
                                manifest,
                                loadBundle,
                            })
                            : yield createComponentLoaders_1.createEagerComponentLoaders({
                                manifest,
                                loadBundle,
                            });
                }));
                yield Promise.all(tasks);
                loaders = {};
                Object.keys(librariesComponents)
                    .map(v => parseInt(v, 10))
                    .sort()
                    .forEach(index => {
                    Object.assign(loaders, librariesComponents[index]);
                });
            });
        }
        if (!options.useLazyLoadersInit) {
            yield createComponentLoaders(context.manifests);
        }
        const loadComponents = (componentNames) => __awaiter(this, void 0, void 0, function* () {
            const pending = componentNames.map((componentName) => __awaiter(this, void 0, void 0, function* () {
                return {
                    componentName,
                    model: yield loaders[componentName](),
                };
            }));
            const models = yield Promise.all(pending);
            return models.reduce((acc, { model, componentName }) => {
                acc[componentName] = model;
                return acc;
            }, {});
        });
        let isContextFulfilled = false;
        let areComponentsLoadersCreated = !options.useLazyLoadersInit;
        const pendingPromises = {};
        return {
            getComponentsLoaders() {
                return loaders;
            },
            getManifestURLs() {
                return [...context.urls];
            },
            getHostBundleAssets() {
                return utils_1.flat(context.manifests.map(({ manifest }) => {
                    const model = manifest.getHostBundleModel();
                    return createComponentLoaders_1.getComponentAssets({ resource: model });
                }));
            },
            loadComponents(componentNames) {
                return __awaiter(this, void 0, void 0, function* () {
                    return loadComponents(componentNames);
                });
            },
            loadAllComponents() {
                const componentNames = Object.keys(loaders);
                return loadComponents(componentNames);
            },
            getLibrariesAssets() {
                return utils_1.flat(context.manifests.map(({ manifest }) => manifest.getLibraryAssets()));
            },
            getRuntime() {
                const _libraries = [
                    ...context.manifests.map(({ manifest }) => manifest.getManifest()),
                ];
                if (registryOverrideLibrary) {
                    _libraries.push(registryOverrideLibrary);
                }
                return {
                    libraries: _libraries,
                };
            },
            ensureComponentLoadersAreCreated() {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!areComponentsLoadersCreated) {
                        if (!pendingPromises.componentsLoaders) {
                            pendingPromises.componentsLoaders = createComponentLoaders(context.manifests);
                        }
                        yield pendingPromises.componentsLoaders;
                        areComponentsLoadersCreated = true;
                        pendingPromises.componentsLoaders = null;
                    }
                });
            },
            ensureManifestsAreLoaded() {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!isContextFulfilled && context.loadManifests) {
                        if (!pendingPromises.librariesManifests) {
                            pendingPromises.librariesManifests = new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                                const manifests = yield context.loadManifests();
                                yield createComponentLoaders(manifests);
                                resolve();
                            }));
                        }
                        yield pendingPromises.librariesManifests;
                        isContextFulfilled = true;
                        pendingPromises.librariesManifests = null;
                    }
                });
            },
        };
    });
}
exports.createRegistry = createRegistry;
//# sourceMappingURL=createRegistry.js.map